import { ConfigService, ProjectService } from '../services';
import { Spinner } from '../services';
import { Alert } from '../services';
import { React, useState, useEffect, useRef } from 'react';
import Pagination from './Pagination';
import {
  CheckCircleIcon,
  DeployIcon,
  FailureIcon,
  FreezeIcon,
  GoToLinkIcon,
  InprocessIcon,
  ListIcon,
  MintTokenIcon,
  UnfreezeIcon,
} from './icons/IconProvider';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import NoDataFound from './NoDataFound';
import TableComponent from './TableComponent';

const OnchainActivities = ({
  load,
  projectId,
  projectDetailsFunction,
  setDeployButtonStatus,
  setMintingListUpdate,
}) => {
  const [isPagination, setPagination] = useState({
    totalPages: 10,
    currentPage: 1,
  });
  const checkInterval = useRef();
  const [projectList, setProjectList] = useState([]);
  const [isLoader, setLoader] = useState(false);
  const [callApi, setIsCallAPi] = useState(false);
  const [filterData, setFilterData] = useState({
    perPage: 10,
    page: 1,
    commonSearch: '',
    sortBy: '',
    project_id: projectId,
  });
  useEffect(() => {
    (async () => {
      await getOnchainActivity();
    })();
  }, [filterData, load]);

  const getOnchainActivity = async () => {
    try {
      setLoader(true);
      let projectResponse = await ProjectService.getMintingList(
        filterData.perPage,
        filterData.page,
        filterData.commonSearch,
        filterData.sortBy,
        filterData.project_id,
      );
      if (projectResponse.status === 'failure') {
        Spinner.hide();
        setLoader(false);
        Alert.error(projectResponse.message);
        return false;
      }
      Spinner.hide();
      setMintingListUpdate(false);
      setPagination({
        totalPages: projectResponse.data.pagination.totalPages,
        currentPage: projectResponse.data.pagination.page,
      });
      setProjectList([...projectResponse.data.records]);
      if (projectResponse.data.records[0]?.activity_status === 2 && callApi) {
        projectDetailsFunction(projectId, false);
      }
      if (
        projectResponse.data.records[0]?.operation === 'deploy' &&
        (projectResponse.data.records[0]?.activity_status === 1 ||
          projectResponse.data.records[0]?.activity_status === 0)
      ) {
        setDeployButtonStatus(true);
      }

      if (
        projectResponse.data.records[0]?.operation === 'deploy' &&
        projectResponse.data.records[0]?.activity_status === 3
      ) {
        setDeployButtonStatus(false);
      }

      let pendingActivity = projectResponse.data.records.find(
        (data) => data.activity_status === 1 || data.activity_status === 0,
      );
      if (pendingActivity && pendingActivity !== undefined) {
        if (checkInterval.current === undefined) {
          checkInterval.current = setInterval(getOnchainActivity, 5000);
        }
      } else {
        checkInterval.current && clearInterval(checkInterval.current);
        checkInterval.current = undefined;
      }

      setIsCallAPi(true);
      setLoader(false);
    } catch (error) {
      Spinner.hide();
      setLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  const handlePagination = (current) => {
    setPagination({ ...isPagination, currentPage: current });
    setFilterData({
      ...filterData,
      page: current,
    });
  };
  const activityIcon = (operation) => {
    switch (operation) {
      case 'mint':
        return <MintTokenIcon />;
      case 'freeze':
        return <FreezeIcon />;
      case 'unfreeze':
        return <UnfreezeIcon />;
      default:
        return <DeployIcon />;
    }
  };

  const activityStatus = (status, index, message) => {
    const statusConfig = {
      2: {
        icon: <CheckCircleIcon />,
        text: 'Completed',
        className: 'text-green-600',
      },
      0: {
        icon: <ListIcon />,
        text: 'In Queue',
        className: 'text-yellowBadge',
      },
      1: {
        icon: <InprocessIcon />,
        text: 'In Process',
        className: 'text-yellowBadge',
      },
      3: {
        icon: <FailureIcon />,
        text: 'Failed',
        className: 'text-red-500',
      },
    };

    const config = statusConfig[status];
    if (!config) return 'Unknown';
    return (
      <>
        <span
          className={`inline-flex items-center justify-center ${config.className} rounded`}
        >
          {config.icon}
          {config.text}
        </span>
        {message && (
          <ReactTooltip
            id={`to_message_address${index}`}
            place="top"
            content={message}
          />
        )}
      </>
    );
  };

  const tableConfig = {
    columns: {
      activity: {
        title: 'Activity',
        render: (rowData) => {
          return (
            <span className="inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded">
              {activityIcon(rowData.operation)}
              {ConfigService.toTitleCase(rowData.operation) || 'NA'}{' '}
            </span>
          );
        },
      },
      qty: {
        title: 'Qty',
        render: (data) => data.quantity || 'NA',
      },
      txn_hash: {
        title: 'Transaction #',
        render: (data, index) => {
          return data?.projectOnchainActivityLogs[0]?.txn_hash ? (
            <a
              rel="noreferrer"
              href={data?.txnHashURL}
              target="_blank"
              className="text-blue-500 inline-flex items-center font-medium pointer-events-auto"
            >
              <GoToLinkIcon />
              <p
                className="font-medium"
                data-tooltip-id={`to_wallet_address${index}`}
              >
                {data?.projectOnchainActivityLogs[0]
                  ? ConfigService.shortenAddress(
                      data?.projectOnchainActivityLogs[0]?.txn_hash,
                    )
                  : 'NA'}
              </p>
              <ReactTooltip
                id={`to_wallet_address${index}`}
                place="bottom"
                content={data?.projectOnchainActivityLogs[0]?.txn_hash}
              />
            </a>
          ) : (
            'NA'
          );
        },
      },
      date: {
        title: 'Date',
        render: (data) => {
          return ConfigService.dateFormat(data.created_at, true);
        },
      },
      status: {
        title: 'Status',
        render: (data) => {
          return activityStatus(
            data.activity_status,
            0,
            data.projectOnchainActivityLogs[0]?.message,
          );
        },
      },
    },
    data: projectList,
  };

  return (
    <>
      <div className="flex flex-col relative overflow-x-auto">
        <TableComponent
          tableConfig={tableConfig}
          isListLoader={isLoader}
          isList={projectList}
        />
        {projectList && projectList.length > 0 && isLoader === false && (
          <Pagination
            total={isPagination.totalPages}
            current={isPagination.currentPage}
            pagination={(crPage) => handlePagination(crPage)}
          />
        )}
        {projectList && projectList.length === 0 && isLoader === false && (
          <>
            <NoDataFound />
          </>
        )}
      </div>
    </>
  );
};

export default OnchainActivities;
